import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';

import { fetchITicketMagicLink } from '../../utils/functions';
import Spinner from '../../components/Spinner';

interface Props {
  idToken: string;
}

/**
 * This page handles redirecting the user to iTicket store,
 * likely after they've been redirected here to authenticate.
 */
function ITicketRedirect(props: Props): JSX.Element {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  const { t, i18n } = useTranslation(['iticket']);
  const { inProgress } = useMsal();

  // Fetch the magic link for creating the cart and authenticating the user.
  // Redirect the user to iticket store if successful.
  useEffect(() => {
    async function getMagicLink(token: string) {
      let url = await fetchITicketMagicLink(token, i18n.language);
      window.sessionStorage.removeItem('postLoginRedirect');

      if (url) {
        const callbackUrl = window.sessionStorage.getItem('callback_url');

        // Redirect to specific url that was given as a parameter.
        if (callbackUrl) {
          const authUrl = new URL(url);
          const code = authUrl.searchParams.get('code') ?? '';
          const userName = authUrl.searchParams.get('userName') ?? '';
          const newRedirect = new URL(callbackUrl);
          newRedirect.searchParams.set('code', code);
          newRedirect.searchParams.set('userName', userName);
          url = newRedirect.toString();
          window.sessionStorage.removeItem('callback_url');
        }

        window.location.href = url;
      } else {
        setError(true);
      }
      setIsLoading(false);
    }

    if (props.idToken && inProgress === InteractionStatus.None) {
      getMagicLink(props.idToken);
    }
  }, [props.idToken, inProgress]);

  return (
    <div className='m-4 p-8 flex justify-center flex-col items-center'>
      {isLoading && (
        <div className=''>
          <p className='mb-8'>{t('Redirecting you back to the store...')}</p>
          <Spinner />
        </div>
      )}
      {error && (
        <p>{t('There was an error getting the link. Please try again.')}</p>
      )}
    </div>
  );
}

export default ITicketRedirect;
